import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { Plus } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, ref } from 'vue';
    import { toast } from 'vue-sonner';
    import { useContraAccountData } from '@/composables/useContraAccountData';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import type { SharedData } from '@/types';
    import VoucherStatementUploadForm from '@/components/vouchers/VoucherStatementUploadForm.vue';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import VoucherStatementUploadAlertDialog from '@/components/vouchers/VoucherStatementUploadAlertDialog.vue';
    import { useVoucherStore } from '@/stores/voucher';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'VoucherStatementUpload',
  setup(__props) {

    const voucherStore = useVoucherStore();

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const uploadFormRef = ref<InstanceType<typeof VoucherStatementUploadForm>>();
    const isOpen = ref(false);

    const { contraAccounts, loadingContraAccounts, fetchContraAccounts } =
        useContraAccountData(companyId);

    const { t } = useI18n();
    const queryClient = useQueryClient();
    const filesProcessing = ref(false);

    const form = useForm({
        company_id: companyId.value,
        contra_account_id: null,
        voucher_statement_id: null,
    });

    const registerUploads = () => {
        form.post(route('v2.voucher.admin.statement.register'), {
            preserveScroll: true,
            onSuccess: () => {
                uploadFormRef.value?.markFilesAsRegistered();
                toast.success(t('vouchers.multi_registration.register_success'));
                queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId] });
            },
            onError: (error: any) => {
                toast.error(t('forms.system_error'));
            },
        });
    };

    const resetModal = () => {
        form.resetAndClearErrors('voucher_statement_id', 'contra_account_id');
        form.voucher_statement_id = null;
        form.contra_account_id = null;
    };

    const openModal = async () => {
        await fetchContraAccounts();
        if (contraAccounts.value.length === 0) {
            toast.success(t('vouchers.statement_upload'), {
                description: t('vouchers.js.input_valid'),
            });
        } else {
            isOpen.value = true;
        }
    };

    const handleCloseAttempt = (newOpenState: boolean) => {
        if (!newOpenState && uploadFormRef.value?.hasUnregisteredFiles) {
            isOpen.value = true;
            voucherStore.toggleStatementUploadAlertModal(true);

            return;
        }
        resetModal();
        isOpen.value = newOpenState;
    };

    const handleFilesProcessing = (processing: boolean) => {
        filesProcessing.value = processing;
    };

    const handleDeleteStatements = () => {
        resetModal();
        isOpen.value = false;
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Dialog), {
      open: isOpen.value,
      "onUpdate:open": [
        _cache[0] || (_cache[0] = ($event: any) => ((isOpen).value = $event)),
        handleCloseAttempt
      ]
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Button), {
          variant: "ghost",
          width: "narrow",
          class: "self-start",
          size: "xs",
          onClick: openModal,
          loading: _unref(loadingContraAccounts)
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('vouchers.statement_upload')) + " ", 1),
            _createVNode(_unref(Plus), { class: "text-accent" })
          ]),
          _: 1
        }, 8, ["loading"]),
        _createVNode(_unref(DialogContent), { class: "sm:max-w-lg" }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('vouchers.statement_upload')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(DialogBody), null, {
              default: _withCtx(() => [
                _createVNode(VoucherStatementUploadForm, {
                  ref_key: "uploadFormRef",
                  ref: uploadFormRef,
                  form: _unref(form),
                  "contra-accounts": _unref(contraAccounts),
                  onFilesProcessing: handleFilesProcessing
                }, null, 8, ["form", "contra-accounts"])
              ]),
              _: 1
            }),
            _createVNode(_unref(DialogFooter), { class: "gap-2 border-t sm:justify-center" }, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogClose), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Button), {
                      variant: "outline",
                      size: "sm",
                      onClick: resetModal
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('forms.cancel')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Button), {
                  disabled: 
                        _unref(form).processing || !_unref(form).data().voucher_statement_id || filesProcessing.value
                    ,
                  loading: _unref(form).processing,
                  size: "sm",
                  onClick: registerUploads
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('button-text.register-economic')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(VoucherStatementUploadAlertDialog, { onDeleteStatements: handleDeleteStatements })
  ], 64))
}
}

})